import { useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { useApiClient, type InferRequest } from '../../hooks/useApiClient'
import { useMatchCacheUtils } from './cacheUtils'
import { useSecurityContext } from '@/components/Navigation/SecurityContext'

export type MatchSetHoldRequest = InferRequest<'/supplier_rec/match/set_hold'>
export type MatchApproveRequest = InferRequest<'/supplier_rec/match/approve'>

export function useMatchStatusActions() {
  const queryClient = useQueryClient()
  const $api = useApiClient()
  const cacheUtils = useMatchCacheUtils(queryClient)
  const { getUser } = useSecurityContext()

  const invalidateMatchQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ['post', '/supplier_rec/match/overview'],
    })
    queryClient.invalidateQueries({
      queryKey: ['matchOverview'],
    })
  }

  const useUnapproveMatch = () => {
    return $api.useMutation('post', '/supplier_rec/match/unapprove', {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })
        message.success('Successfully unapproved match')
      },
      onError: () => {
        message.error(`Failed to unapprove match. Please try again.`)
      },
    })
  }

  const useApproveMatch = () => {
    return $api.useMutation('post', '/supplier_rec/match/approve', {
      onMutate: async (request) => {
        await cacheUtils.cancelMatchQueries()

        await queryClient.cancelQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        const previousData = cacheUtils.getPreviousQueryData()
        const previousDetailData = queryClient.getQueriesData({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        const matchIds = request.body?.match_ids || []
        const reasonCode = request.body?.approval_reason

        // Get current match data to determine original status
        const currentMatches: any[] = []
        queryClient
          .getQueriesData({ queryKey: ['supplier_rec_match_overview_open'] })
          .forEach(([_, data]: [unknown, any]) => {
            if (data?.rows) currentMatches.push(...data.rows)
          })

        // Create updated match data with new status
        const updatedMatches = matchIds.map((id) => {
          const originalMatch = currentMatches.find((match) => match.id === id)
          return {
            id,
            status: 'approved' as const,
            updated_at: new Date().toISOString(),
            approval_reason: reasonCode,
            ...(originalMatch || {}), // Include original match data
          }
        })

        // Use transition handler for cross-query movement
        matchIds.forEach((id) => {
          const originalMatch = currentMatches.find((match) => match.id === id)
          const originalStatus = originalMatch?.status || 'unapproved'
          const updatedMatch = updatedMatches.find((match) => match.id === id)

          if (updatedMatch) {
            cacheUtils.updateMatchStatusTransition([id], originalStatus, 'approved', [updatedMatch])
          }
        })

        queryClient.setQueriesData(
          { queryKey: ['post', '/supplier_rec/match/detail'] },
          (oldData: { match_id?: string; status?: string; [key: string]: unknown } | undefined) => {
            if (!oldData) return oldData
            const isMatchInDetail = matchIds.includes(oldData.match_id)
            return isMatchInDetail ? { ...oldData, status: 'approved' } : oldData
          }
        )

        return { previousData, previousDetailData, request }
      },
      onSuccess: (response, request, context) => {
        const cacheUpdate = cacheUtils.processApiResponse(response, 'approve')
        cacheUtils.updateMatchOverviewCache(cacheUpdate)

        // Invalidate match detail queries
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        // Invalidate ALL match overview queries (including SharedHomeContext)
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0]
            return (
              key === 'supplier_rec_match_overview_open' ||
              key === 'supplier_rec_match_overview_approved' ||
              key === 'supplier_rec_match_overview_voided'
            )
          },
        })

        const matchCount = request.body?.match_ids?.length || 0
        message.success(`Successfully approved ${matchCount} match${matchCount > 1 ? 'es' : ''}`)
      },
      onError: (error, request, context: { previousData?: [unknown, unknown][] } | undefined) => {
        if (context?.previousData) {
          cacheUtils.rollbackQueryData(context.previousData)
        }

        if (context?.previousDetailData && context.previousDetailData.length > 0) {
          context.previousDetailData.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey, data)
          })
        }

        message.error('Failed to approve match. Please try again.')
      },
    })
  }

  const useSetMatchHold = () => {
    return $api.useMutation('post', '/supplier_rec/match/set_hold', {
      onMutate: async (request) => {
        await cacheUtils.cancelMatchQueries()
        const user = await getUser()

        await queryClient.cancelQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        const previousData = cacheUtils.getPreviousQueryData()
        const previousDetailData = queryClient.getQueriesData({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        const matchIds = request.body?.match_ids || []
        const isHold = request.body?.hold !== false
        const reason = request.body?.hold_reason ?? ''

        const optimisticUpdate = isHold
          ? cacheUtils.createOptimisticHold(matchIds, reason, user?.username)
          : cacheUtils.createOptimisticUnhold(matchIds)
        cacheUtils.updateMatchOverviewCache(optimisticUpdate)

        queryClient.setQueriesData(
          { queryKey: ['post', '/supplier_rec/match/detail'] },
          (oldData: { match_id?: string; status?: string; [key: string]: unknown } | undefined) => {
            if (!oldData) return oldData
            const isMatchInDetail = matchIds.includes(oldData.match_id)
            return isMatchInDetail ? { ...oldData, status: isHold ? 'on_hold' : 'pending' } : oldData
          }
        )

        return { previousData, previousDetailData, request }
      },
      onSuccess: (response, request, context) => {
        const actionType = request.body?.hold !== false ? 'hold' : 'unhold'
        const cacheUpdate = cacheUtils.processApiResponse(response, actionType)
        cacheUtils.updateMatchOverviewCache(cacheUpdate)

        // Invalidate match detail queries
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        // Invalidate ALL match overview queries (including SharedHomeContext)
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0]
            return (
              key === 'supplier_rec_match_overview_open' ||
              key === 'supplier_rec_match_overview_approved' ||
              key === 'supplier_rec_match_overview_voided'
            )
          },
        })

        const matchCount = request.body?.match_ids?.length || 0
        const isHold = request.body?.hold !== false
        const action = isHold ? 'marked as on hold' : 'removed from hold'
        message.success(`Successfully ${action} ${matchCount} match${matchCount > 1 ? 'es' : ''}`)
      },
      onError: (error, request, context: { previousData?: [unknown, unknown][] } | undefined) => {
        if (context?.previousData) {
          cacheUtils.rollbackQueryData(context.previousData)
        }

        if (context?.previousDetailData && context.previousDetailData.length > 0) {
          context.previousDetailData.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey, data)
          })
        }

        const isHold = request.body?.hold !== false
        const action = isHold ? 'mark match as on hold' : 'remove match from hold'
        message.error(`Failed to ${action}. Please try again.`)
      },
    })
  }

  return {
    useUnapproveMatch,
    useApproveMatch,
    useSetMatchHold,
    invalidateMatchQueries,
  }
}
